import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Space,
  Tag,
  Popconfirm,
  message,
  Card,
  Typography,
  Switch,
  Empty,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import {
  getShippingProfiles,
  createShippingProfile,
  updateShippingProfile,
  deleteShippingProfile,
  saveShippingProfileRates,
} from "../../api/shippingProfilesApi";
const ALL_GOVERNORATES = [
  "القاهرة", "الجيزة", "الإسكندرية", "الدقهلية", "البحيرة", "الفيوم",
  "الغربية", "الإسماعيلية", "المنوفية", "المنيا", "القليوبية", "السويس",
  "دمياط", "الشرقية", "كفر الشيخ", "بني سويف", "بورسعيد", "أسيوط",
  "سوهاج", "قنا", "مطروح", "شمال سيناء", "جنوب سيناء", "الوادي الجديد",
  "البحر الأحمر", "الأقصر", "أسوان",
];
const { Title, Text } = Typography;

export default function ShippingProfilesPage() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);

  // Profile create/edit
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [profileForm] = Form.useForm();

  // Rates management
  const [ratesModalOpen, setRatesModalOpen] = useState(false);
  const [activeProfile, setActiveProfile] = useState(null);
  const [ratesForm] = Form.useForm();
  const [savingRates, setSavingRates] = useState(false);

  const fetchProfiles = async () => {
    setLoading(true);
    try {
      const res = await getShippingProfiles();
      const data = res.data;
      setProfiles(
        Array.isArray(data) ? data : data.results ?? data.data?.results ?? data.data ?? []
      );
    } catch {
      message.error("فشل تحميل قائمة الـ Shipping Profiles.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfiles();
  }, []);

  // ── Profile CRUD ────────────────────────────────────────────────────────
  const openAddProfile = () => {
    setEditingProfile(null);
    profileForm.resetFields();
    profileForm.setFieldsValue({ is_active: true });
    setProfileModalOpen(true);
  };
  const handleFillAllGovernorates = () => {
  const currentRates = ratesForm.getFieldValue("rates") || [];

  // خريطة بالمحافظات الموجودة بالفعل عشان نحافظ على أسعارها
  const existingMap = {};
    currentRates.forEach((r) => {
      if (r?.governorate?.trim()) {
        existingMap[r.governorate.trim()] = r.cost ?? 0;
      }
    });

    const merged = ALL_GOVERNORATES.map((gov) => ({
      governorate: gov,
      cost: existingMap[gov] ?? 0,
    }));

    ratesForm.setFieldsValue({ rates: merged });
    message.success("تم إضافة كل المحافظات، عدّل الأسعار وابعت حفظ.");
  };

  const openEditProfile = (record) => {
    setEditingProfile(record);
    profileForm.setFieldsValue({ name: record.name, is_active: record.is_active });
    setProfileModalOpen(true);
  };

  const handleSaveProfile = async (values) => {
    try {
      if (editingProfile) {
        await updateShippingProfile(editingProfile.id, values);
        message.success("تم تحديث الـ Profile.");
      } else {
        await createShippingProfile(values);
        message.success("تم إنشاء الـ Profile.");
      }
      setProfileModalOpen(false);
      fetchProfiles();
    } catch (err) {
      message.error(err.response?.data?.message || "حدث خطأ.");
    }
  };

  const handleDeleteProfile = async (id) => {
    try {
      await deleteShippingProfile(id);
      message.success("تم الحذف. أي منتج كان مربوط بالـ Profile ده هيرجع تلقائيًا للشحن العادي.");
      fetchProfiles();
    } catch {
      message.error("فشل الحذف.");
    }
  };

  // ── Rates management ────────────────────────────────────────────────────
  const openRates = (record) => {
    setActiveProfile(record);
    ratesForm.setFieldsValue({
      rates: record.rates?.length
        ? record.rates.map((r) => ({ governorate: r.governorate, cost: Number(r.cost) }))
        : [{ governorate: "", cost: 0 }],
    });
    setRatesModalOpen(true);
  };

  const handleSaveRates = async (values) => {
    const payload = (values.rates || [])
      .filter((r) => r.governorate && r.governorate.trim())
      .map((r) => ({ governorate: r.governorate.trim(), cost: r.cost }));

    if (payload.length === 0) {
      message.warning("أضف محافظة واحدة على الأقل.");
      return;
    }

    setSavingRates(true);
    try {
      await saveShippingProfileRates(activeProfile.id, payload);
      message.success("تم حفظ أسعار الشحن.");
      setRatesModalOpen(false);
      fetchProfiles();
    } catch (err) {
      message.error(err.response?.data?.message || "فشل الحفظ.");
    } finally {
      setSavingRates(false);
    }
  };

  const columns = [
    {
      title: "الاسم",
      dataIndex: "name",
      render: (name) => <Text strong>{name}</Text>,
    },
    {
      title: "الحالة",
      dataIndex: "is_active",
      render: (active) => (
        <Tag color={active ? "success" : "default"}>{active ? "مفعّل" : "غير مفعّل"}</Tag>
      ),
    },
    {
      title: "عدد المحافظات المسعّرة",
      dataIndex: "rates",
      render: (rates) => rates?.length || 0,
    },
    {
      title: "الإجراءات",
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<SettingOutlined />} onClick={() => openRates(record)}>
            إدارة الأسعار
          </Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditProfile(record)}>
            تعديل
          </Button>
          <Popconfirm
            title="حذف هذا الـ Profile؟"
            description="أي منتج مربوط بيه هيرجع تلقائيًا للشحن العادي."
            onConfirm={() => handleDeleteProfile(record.id)}
            okText="نعم"
            cancelText="لا"
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              حذف
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>
          Shipping Profiles (شحن مخصص)
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openAddProfile}>
          إضافة Profile جديد
        </Button>
      </div>

      <Card>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={profiles}
          loading={loading}
          pagination={{ pageSize: 15 }}
          locale={{
            emptyText: (
              <Empty description="لا يوجد Shipping Profiles بعد. أنشئ واحد وحدد له أسعار مخصصة لكل محافظة." />
            ),
          }}
        />
      </Card>

      {/* Profile create/edit modal */}
      <Modal
        open={profileModalOpen}
        title={editingProfile ? "تعديل الـ Profile" : "إضافة Profile جديد"}
        onCancel={() => setProfileModalOpen(false)}
        onOk={() => profileForm.submit()}
        okText="حفظ"
        cancelText="إلغاء"
        style={{ direction: "rtl" }}
      >
        <Form form={profileForm} layout="vertical" onFinish={handleSaveProfile}>
          <Form.Item
            name="name"
            label="اسم الـ Profile"
            rules={[{ required: true, message: "أدخل اسم الـ Profile" }]}
          >
            <Input placeholder="مثال: منتجات تقيلة / أجهزة كهربائية" />
          </Form.Item>
          <Form.Item name="is_active" label="مفعّل" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Rates management modal */}
      <Modal
        open={ratesModalOpen}
        title={`أسعار الشحن — ${activeProfile?.name || ""}`}
        onCancel={() => setRatesModalOpen(false)}
        onOk={() => ratesForm.submit()}
        okText="حفظ الأسعار"
        cancelText="إلغاء"
        confirmLoading={savingRates}
        width={640}
        style={{ direction: "rtl" }}
      >
        <Form form={ratesForm} layout="vertical" onFinish={handleSaveRates}>
          <div style={{ marginBottom: 12 }}>
            <Button onClick={handleFillAllGovernorates} block>
              + تعبئة كل المحافظات المصرية دفعة واحدة
            </Button>
          </div>
          <Form.List name="rates">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <Space key={key} align="baseline" style={{ display: "flex", marginBottom: 8 }}>
                    <Form.Item
                      {...rest}
                      name={[name, "governorate"]}
                      rules={[{ required: true, message: "اسم المحافظة" }]}
                    >
                      <Input placeholder="اسم المحافظة" style={{ width: 220 }} />
                    </Form.Item>
                    <Form.Item
                      {...rest}
                      name={[name, "cost"]}
                      rules={[{ required: true, message: "التكلفة" }]}
                    >
                      <InputNumber min={0} placeholder="التكلفة" addonAfter="ج.م" />
                    </Form.Item>
                    <Button danger type="text" onClick={() => remove(name)}>
                      حذف
                    </Button>
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add({ governorate: "", cost: 0 })} block>
                  + إضافة محافظة
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
}